import { z } from "zod";
import { DEFAULT_ROOM_SETTINGS } from "./types.js";
import { BINGO_CATEGORY_IDS, DEFAULT_BINGO_SETTINGS } from "./bingo/types.js";
const bingoSettingsSchema = z.object({
    gridSize: z.union([
        z.literal(3),
        z.literal(4),
        z.literal(5),
        z.literal(6),
        z.literal(7),
    ]).default(DEFAULT_BINGO_SETTINGS.gridSize),
    timerSeconds: z.number().int().min(0).max(180).nullable().default(DEFAULT_BINGO_SETTINGS.timerSeconds),
    winConditions: z
        .object({
        horizontal: z.boolean().default(true),
        vertical: z.boolean().default(true),
        diagonal: z.boolean().default(true),
    })
        .default(DEFAULT_BINGO_SETTINGS.winConditions),
    exactYearPower: z.boolean().default(true),
    categories: z.array(z.enum(BINGO_CATEGORY_IDS)).min(1).max(25).default(DEFAULT_BINGO_SETTINGS.categories),
    preset: z.enum(["easy", "normal", "expert", "custom"]).default("easy"),
});
const roomSettingsSchema = z.object({
    winTarget: z.number().int().min(1).max(50).default(DEFAULT_ROOM_SETTINGS.winTarget),
    maxPlayers: z.number().int().min(2).max(12).default(DEFAULT_ROOM_SETTINGS.maxPlayers),
    roundDurationSeconds: z.number().int().min(15).max(180).default(DEFAULT_ROOM_SETTINGS.roundDurationSeconds),
    singleListen: z.boolean().default(false),
    minYear: z.number().int().optional(),
    maxYear: z.number().int().optional(),
    password: z.string().min(1).max(32).optional(),
    mode: z.enum(["individual", "shared"]).default("individual"),
    includeMusicGuess: z.boolean().default(false),
    hintsEnabled: z.boolean().default(false),
    maxHintsPerPlayer: z.number().int().min(1).max(10).default(3),
    continueAfterWin: z.boolean().default(true),
    gameKind: z.enum(["timeline", "bingo"]).default("timeline"),
    bingo: bingoSettingsSchema.optional(),
});
export const ClientEvents = {
    "room:create": z.object({
        nickname: z.string().min(2).max(24),
        settings: roomSettingsSchema.partial().optional(),
    }),
    "room:join": z.object({
        code: z.string().length(6),
        nickname: z.string().min(2).max(24),
        password: z.string().optional(),
        reconnectToken: z.string().optional(),
        asSpectator: z.boolean().optional(),
    }),
    "room:leave": z.object({}),
    "room:sync": z.object({}),
    "player:set-ready": z.object({ ready: z.boolean() }),
    "game:start": z.object({}),
    "audio:request-play": z.object({ roundId: z.string() }),
    "audio:playback-started": z.object({ roundId: z.string() }),
    "audio:playback-failed": z.object({ roundId: z.string() }),
    "round:place-card": z.object({ roundId: z.string(), insertIndex: z.number().int().min(0) }),
    "round:confirm-placement": z.object({ roundId: z.string() }),
    "round:continue": z.object({ roundId: z.string() }),
    "round:use-hint": z.object({ roundId: z.string() }),
    "game:restart": z.object({}),
    "game:rematch": z.object({}),
    "host:update-settings": z.object({ settings: roomSettingsSchema.partial() }),
    "host:kick-player": z.object({ playerId: z.string() }),
    "bingo:submit-answer": z.object({
        roundId: z.string(),
        answer: z.union([z.string().max(200), z.number(), z.boolean()]),
    }),
    "bingo:select-cell": z.object({
        roundId: z.string(),
        cellIndex: z.number().int().min(0).max(48),
    }),
    "bingo:use-power": z.object({
        roundId: z.string(),
        targetPlayerId: z.string(),
        cellIndex: z.number().int().min(0).max(48),
    }),
    "bingo:skip-power": z.object({ roundId: z.string() }),
    "bingo:vote-answer": z.object({
        roundId: z.string(),
        targetPlayerId: z.string(),
    }),
};
export const ServerEvents = {
    "room:state": z.custom(),
    "room:error": z.object({ message: z.string() }),
    "game:state": z.custom(),
    "round:prepared": z.object({
        roundId: z.string(),
        activePlayerId: z.string(),
        roundNumber: z.number(),
    }),
    "audio:play": z.object({
        roundId: z.string(),
        scheduledAt: z.number(),
        source: z.object({
            roundId: z.string(),
            url: z.string(),
            clipStartSeconds: z.number(),
            clipDurationSeconds: z.number(),
            sourceType: z.enum(["LOCAL_AUDIO", "REMOTE_AUDIO", "YOUTUBE"]),
            streamProvider: z.enum(["youtube", "soundcloud"]).optional(),
            streamSourceId: z.string().optional(),
        }),
    }),
    "round:reveal": z.custom(),
    "round:hint-revealed": z.object({
        roundId: z.string(),
        decade: z.number().int(),
        hintsRemaining: z.number().int().min(0),
    }),
    "player:reconnected": z.object({ playerId: z.string(), nickname: z.string() }),
    "game:finished": z.object({
        winnerId: z.string(),
        winnerNickname: z.string(),
        winnerIds: z.array(z.string()).optional(),
        winnerNicknames: z.array(z.string()).optional(),
    }),
    "notification": z.object({ message: z.string(), level: z.enum(["info", "success", "warning", "error"]).optional() }),
    "room:kicked": z.object({ message: z.string() }),
};
export function parseClientEvent(event, payload) {
    return ClientEvents[event].parse(payload);
}
